import type { VolunteerLocationItemDto } from '@sola/skippy-search-sdk/lib/schema.ts';
import { formatSlotTime } from '@sola/utils/date.ts';

/**
 * Subset of the volunteer location DTO needed for datalayer tracking.
 * Sourced from the SDK schema rather than redefined so the fields stay in
 * sync with the search response shape.
 */
export type VolunteerLocationInfo = Pick<
  VolunteerLocationItemDto,
  'eventId' | 'locationName' | 'city' | 'state' | 'startDate' | 'endDate'
> &
  Pick<Partial<VolunteerLocationItemDto>, 'roles'>;

export interface VolunteerSlotInfo {
  id: string;
  startDate: string;
  endDate: string;
  description: string | null;
}

export interface VolunteerTrackingParams {
  stepIndex: number;
  location: VolunteerLocationInfo;
  slots: VolunteerSlotInfo[];
  assignments: Record<string, string[]>;
  selectedContactIds: string[];
  /** True when the event belongs to a tour → adds 'Tour' to content_tags. */
  isTour?: boolean;
  /** Additional content tags to append (event taxonomy, TBD). */
  extraTags?: string[];
}

/* eslint-disable @typescript-eslint/naming-convention */

/** Maps a VolunteerLocationInfo to the flat datalayer fields. */
export const mapLocationToDatalayer = (loc: VolunteerLocationInfo) => ({
  content_id: loc.eventId,
  content_title: loc.locationName,
  event_city: loc.city,
  event_state: loc.state,
  event_start_date: loc.startDate.slice(0, 10),
  event_end_date: loc.endDate.slice(0, 10),
  event_roles: loc.roles ?? [],
});

/**
 * Builds the `content_tags` array for a volunteer event. `'Tour'` leads when
 * the item belongs to a tour, followed by any item-specific tags (event
 * taxonomy, TBD). Returns undefined when there are no tags, so callers can
 * spread it to omit the key entirely for non-tour single events.
 */
export const buildContentTags = (
  isTour?: boolean,
  extraTags: string[] = [],
): string[] | undefined => {
  const tags = [...(isTour ? ['Tour'] : []), ...extraTags];
  return tags.length ? tags : undefined;
};

/** The datalayer payload for a volunteer `view_content` impression. */
export interface VolunteerViewContent {
  content_id: string;
  content_title: string;
  content_tags?: string[];
  event_city?: string | null;
  event_state?: string | null;
  event_start_date: string;
  event_end_date: string;
  event_roles: string[];
}

/**
 * Builds the `view_content` payload for a standalone volunteer page, shaped by
 * whether the page is a single event or a tour (a group of >1 dated events).
 *
 * - Single event: carries `event_city`/`event_state` and the event's own
 *   start/end dates and roles. No `content_tags`.
 * - Tour: omits city/state (a tour spans locations), tags with `['Tour']`,
 *   spans the date range across all tour events (earliest start → latest end),
 *   and aggregates de-duplicated roles across them.
 *
 * `content_url` is added by the caller since it depends on the browser
 * location. Extra tags beyond `['Tour']` are expected later; callers can pass
 * `extraTags` to append them.
 */
const earliest = (a: string, b: string): string => (a <= b ? a : b);
const latest = (a: string, b: string): string => (a >= b ? a : b);
export const buildVolunteerViewContent = (input: {
  location: VolunteerLocationInfo;
  /** The tour's events. When length > 1 the page is treated as a tour. */
  tourLocations?: VolunteerLocationInfo[];
  /** Overrides content_title (e.g. the tour name on a tour page). */
  title?: string | null;
  /** Additional content tags to append (taxonomy TBD). */
  extraTags?: string[];
}): VolunteerViewContent => {
  const { location, tourLocations, title, extraTags = [] } = input;
  const isTour = (tourLocations?.length ?? 0) > 1;

  const contentTitle = title || location.locationName;

  if (!isTour) {
    return {
      content_id: location.eventId,
      content_title: contentTitle,
      event_city: location.city,
      event_state: location.state,
      event_start_date: location.startDate.slice(0, 10),
      event_end_date: location.endDate.slice(0, 10),
      event_roles: location.roles ?? [],
    };
  }

  const events = tourLocations ?? [];
  // ISO 8601 strings sort chronologically under lexicographic comparison.
  // These are date strings, not numbers — string comparison, never Math.*.
  const startDate = events
    .map((e) => e.startDate)
    .reduce(earliest, events[0].startDate);
  const endDate = events
    .map((e) => e.endDate)
    .reduce(latest, events[0].endDate);
  const roles = [...new Set(events.flatMap((e) => e.roles ?? []))];

  return {
    content_id: location.eventId,
    content_title: contentTitle,
    content_tags: buildContentTags(true, extraTags),
    event_start_date: startDate.slice(0, 10),
    event_end_date: endDate.slice(0, 10),
    event_roles: roles,
  };
};

/** Step labels for the volunteer registration funnel, indexed by stepIndex. */
export const volunteerStepNames = [
  'People',
  'Select Shifts',
  'Sign Waivers',
  'Confirm Info',
];

export interface VolunteerEventFields {
  step_name: string;
  content_tags?: string[];
  event_shifts: string[];
  event_roles: string[];
  number_of_guests: string;
}

/**
 * Derives the datalayer fields for a volunteer registration event from the
 * tracking params. Pure function with no side effects — kept separate from
 * pushVolunteerEvent so the field derivation can be unit tested directly.
 *
 * Shifts and roles are drawn only from the slots the user was actually
 * assigned to (via `assignments`), de-duplicated, and formatted.
 */
export const buildVolunteerEventFields = (
  params: VolunteerTrackingParams,
): VolunteerEventFields => {
  const assignedSlotIds = new Set(Object.values(params.assignments).flat());
  const assignedSlots = params.slots.filter((s) => assignedSlotIds.has(s.id));

  const eventShifts = assignedSlots.length
    ? [
        ...new Set(
          assignedSlots.map(
            (s) =>
              `${formatSlotTime(s.startDate)} - ${formatSlotTime(s.endDate)}`,
          ),
        ),
      ]
    : [];

  const eventRoles = assignedSlots.length
    ? ([
        ...new Set(assignedSlots.map((s) => s.description).filter(Boolean)),
      ] as string[])
    : [];

  const numberOfGuests = params.selectedContactIds.length
    ? String(params.selectedContactIds.length)
    : '';

  const contentTags = buildContentTags(params.isTour, params.extraTags);

  return {
    step_name: volunteerStepNames[params.stepIndex] ?? '',
    ...(contentTags ? { content_tags: contentTags } : {}),
    event_shifts: eventShifts,
    event_roles: eventRoles,
    number_of_guests: numberOfGuests,
  };
};

// ─── List / search impression helpers ──────────────────────────────────────

/** Content list identity used for the generic (non-tour) volunteer listing. */
export const defaultVolunteerListId = 'volunteer-upcoming-events';
export const defaultVolunteerListName = 'Volunteer Upcoming Events';

/** The resolved content list id/name for a volunteer listing impression. */
export interface VolunteerListIdentity {
  contentListId: string;
  contentListName: string;
}

/**
 * Resolves the content_list_id / content_list_name shared by both volunteer
 * list impressions (`view_content_list` and `volunteer_search`).
 *
 * - Generic listing (no slug, or a tour with no resolved `tourName` yet — e.g.
 *   an empty result set): the fixed upcoming-events identity.
 * - Tour listing (slug and tourName present): the generic identity extended
 *   with the tour as a descriptor — `[defaultId]-[slug]` and
 *   `[defaultName]: [tourName]`.
 */
export const resolveVolunteerListIdentity = (input: {
  slug?: string;
  tourName?: string | null;
}): VolunteerListIdentity => {
  if (!input.tourName || !input.slug) {
    return {
      contentListId: defaultVolunteerListId,
      contentListName: defaultVolunteerListName,
    };
  }
  return {
    contentListId: `${defaultVolunteerListId}-${input.slug}`,
    contentListName: `${defaultVolunteerListName}: ${input.tourName}`,
  };
};

/**
 * Builds the `select_content` payload for a volunteer card click. Two signals,
 * kept separate on purpose:
 *
 * - `list` is the LIST context the card was selected from (not the clicked
 *   card). It drives `content_list_id`/`content_list_name`, matching the list's
 *   impression events. On the generic listing it's absent, so the generic list
 *   identity is used even when the clicked card is itself a tour.
 * - `isTour` describes the selected ITEM: true when it belongs to a tour, which
 *   adds `'Tour'` to `content_tags`. This holds both for a tour clicked from a
 *   listing and for an individual event clicked within a tour. `extraTags`
 *   carries the item's own tags (event taxonomy, TBD).
 *
 * `content_url` (the destination page) is added by the caller.
 */
export const buildVolunteerSelectContent = (input: {
  location: VolunteerLocationInfo;
  list?: { slug?: string; tourName?: string | null };
  isTour?: boolean;
  extraTags?: string[];
}) => {
  const identity = resolveVolunteerListIdentity({
    slug: input.list?.slug,
    tourName: input.list?.tourName,
  });
  const contentTags = buildContentTags(input.isTour, input.extraTags);

  return {
    content_list_id: identity.contentListId,
    content_list_name: identity.contentListName,
    ...mapLocationToDatalayer(input.location),
    ...(contentTags ? { content_tags: contentTags } : {}),
  };
};

/**
 * Builds the `volunteer_registration_add_guest` payload: the guest's
 * relationship and age, the event's mapped fields, and `content_tags: ['Tour']`
 * when the event belongs to a tour. `content_url` is added by the caller.
 */
export const buildVolunteerGuestContent = (input: {
  location: VolunteerLocationInfo;
  guestRelationship: string;
  guestAge: string;
  isTour?: boolean;
  extraTags?: string[];
}) => {
  const contentTags = buildContentTags(input.isTour, input.extraTags);

  return {
    guest_relationship: input.guestRelationship,
    guest_age: input.guestAge,
    ...mapLocationToDatalayer(input.location),
    ...(contentTags ? { content_tags: contentTags } : {}),
  };
};

/** Dedupe key for a rendered list: page + ordered event ids. */
export const buildVolunteerListSignature = (
  page: number,
  locations: Pick<VolunteerLocationInfo, 'eventId'>[],
): string => `${page}:${locations.map((loc) => loc.eventId).join(',')}`;

/**
 * Returns the new signature if view_content_list should fire, or null when the
 * batch is empty or unchanged. The caller owns the last-signature state.
 */
export const resolveVolunteerListView = (
  page: number,
  locations: VolunteerLocationInfo[],
  lastSignature: string | null,
): { signature: string } | null => {
  if (locations.length === 0) {
    return null;
  }
  const signature = buildVolunteerListSignature(page, locations);
  if (signature === lastSignature) {
    return null;
  }
  return { signature };
};

/** The sort/query pair that identifies a distinct volunteer search. */
export interface VolunteerSearchFilters {
  sortBy: string;
  query: string;
}

/**
 * Decides whether a `volunteer_search` track should fire for the current
 * settled sort/query. Returns the filters to report when they differ from the
 * last tracked pair, or null when nothing changed.
 *
 * Comparing against the last *tracked* pair (rather than latching a one-way
 * "user has changed filters" flag) means reverting a filter to its previous
 * value doesn't refire a redundant search, and only genuine transitions fire.
 */
export const resolveVolunteerSearchTrack = (
  current: VolunteerSearchFilters,
  lastTracked: VolunteerSearchFilters | null,
): VolunteerSearchFilters | null => {
  if (
    lastTracked !== null &&
    lastTracked.sortBy === current.sortBy &&
    lastTracked.query === current.query
  ) {
    return null;
  }
  return { sortBy: current.sortBy, query: current.query };
};

/** Builds volunteer_search params, sharing the tour/non-tour list identity. */
export const buildVolunteerSearchParams = (input: {
  sortBy: string;
  query: string;
  slug?: string;
  tourName?: string | null;
  locations: VolunteerLocationInfo[];
}) => {
  const identity = resolveVolunteerListIdentity({
    slug: input.slug,
    tourName: input.tourName,
  });
  return {
    contentListName: identity.contentListName,
    contentListId: identity.contentListId,
    eventPostalCode: input.query,
    sortBy: input.sortBy,
    locations: input.locations,
  };
};
